import { TutorialDefinition } from '../TutorialEngine.js';
import { AntennasRenderOptions } from './AntennasTutorialAdapter.js';
import { COLORS } from '../../rendering/Renderer.js';

type AntennasScene = {
    gameState: string;
    renderOptions: AntennasRenderOptions;
    annotations: TutorialDefinition['scenes'][number]['annotations'];
    animate?: {
        interval: number;
        loop: boolean;
        frames: string[];
    };
};

// Grid: 6/1-3:1D1,2A2,3E3
// Full solution: B1B2W,F2E2S,E0E1W

const scenes: AntennasScene[] = [
    // Scene 1: Goal — what a solved puzzle looks like
    {
        gameState: 'B1B2W,F2E2S,E0E1W',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'In Antennas, form a cycle\nthrough all digits', cell: 'C4', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 2: Antenna anatomy
    {
        gameState: 'B1B2B3E,F3F4S',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Receiver\n(signal input)', cell: 'B0', anchor: 'center' },
            { type: 'text-label', text: 'Body', cell: 'B2', anchor: 'center' },
            { type: 'text-label', text: 'Emitter\n(signal output)', cell: 'B4', anchor: 'center' },
            { type: 'text-label', text: 'Signal', cell: 'D3', anchor: 'near-above' },
        ],
    },

    // Scene 3: Signals wrap, antennas can bend, they block signals
    {
        gameState: 'C2C3S,B4C4D4N,D0C0C1B1B2W',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Signals wrap\naround the grid', cell: 'E1', anchor: 'center' },
            { type: 'text-label', text: 'Antennas\ncan bend', cell: 'C0', anchor: 'left' },
            { type: 'text-label', text: 'Signals are blocked\nby antenna bodies', cell: 'C5', anchor: 'center', color: COLORS.amber },
        ],
    },

    // Scene 4: Antenna building
    {
        gameState: 'E2D2S',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click an empty cell\nto start building', cell: 'E0', anchor: 'center' },
            { type: 'text-label', text: 'Click cells in line\nto extend', cell: 'B3', anchor: 'center' },
            { type: 'text-label', text: 'Click the end\nto finish', cell: 'D5', anchor: 'center' },
            { type: 'pulse', cell: 'C1', color: COLORS.teal },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                'E2D2S',
                'E2D2S+C1',
                'E2D2S+C1,B1',
                'E2D2S+C1,B1,B2',
                'E2D2S+C1,B1,B2,B2',
            ],
        },
    },

    // Scene 5: Click a receiver to delete, click an emitter to cycle directions
    {
        gameState: 'E2D2S,B1B2E,F1F2F3F4E',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a receiver\nto delete', cell: 'E0', anchor: 'center' },
            { type: 'text-label', text: 'Click an emitter\nto cycle direction', cell: 'B3', anchor: 'center' },
            { type: 'pulse', cell: 'F1', color: COLORS.amber },
            { type: 'pulse', cell: 'B2', color: COLORS.teal },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                'E2D2S,B1B2E,F1F2F3F4E',
                'E2D2S,B1B2E,F1F2F3F4E+F1',
                'E2D2S,B1B2E,F1F2F3F4E+F1,B2',
                'E2D2S,B1B2E,F1F2F3F4E+F1,B2,B2',
            ],
        },
    },

    // Scene 6: Badges show what should be crossed before receiver
    {
        gameState: 'D0E0S,C5D5N,E5F5F4N',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Badge = expected\nprevious digit', cell: 'B1', anchor: 'center' },
            { type: 'text-label', text: 'Amber = wrong\nsignal received', cell: 'D0', anchor: 'far-left', color: COLORS.amber },
            { type: 'text-label', text: 'Grey = no\nsignal received', cell: 'C5', anchor: 'far-above', color: COLORS.gray },
            { type: 'text-label', text: 'Teal = correct\nsignal received', cell: 'B3', anchor: 'center', color: COLORS.teal },
        ],
    },

    // Scene 7: One signal, one digit
    {
        gameState: 'F1F2E,E5F5F4F3W,C2D2E2S',
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Each signal must\ncross one digit', cell: 'C0', anchor: 'center' },
            { type: 'text-label', text: 'Each digit must be\ncrossed by one signal', cell: 'C5', anchor: 'center' },
            { type: 'text-label', text: 'Digit 3 crossed\nby two signals', cell: 'E3', anchor: 'left', color: COLORS.red },
        ],
    },

];

/**
 * Antennas tutorial: 6 scenes on a 6x6 grid with digits 1-3.
 * See docs/tutorials/antennas.md for detailed scene descriptions.
 */
export const ANTENNAS_TUTORIAL: TutorialDefinition = {
    gameType: 'antennas',
    gridCode: '6/1-3:1D1,2A2,3E3',
    scenes,
};
